/**
 * Bosh sahifa qurilish qismlari (redesign v2): sarlavha, ko'rsatkich kartalari, vazifalar, e'tibor signallari,
 * pipeline lentasi, reyting, oxirgi o'zgarishlar, ustunli grafiklar, pul va voronka. Uch rol bir xil qismlardan
 * turlicha yig'iladi (OwnerHome, RecruiterHome, ManagerHome). Matnlar: i18n namespace "home".
 */
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Check, ChevronRight, Phone } from "lucide-react";
import { cn, dayjs, formatWhen } from "@/lib/utils";
import { useCountUp } from "@/lib/useCountUp";
import { callPhone, copyText, openInNewTab } from "@/lib/clipboard";
import { useReminderAction } from "@/features/drivers/api";
import type { DriverListItem, DriverStatus, Reminder } from "@/features/drivers/types";
import { stageMonthLink } from "@/features/drivers/stageMonth";
import { PIPELINE_ORDER } from "@/features/drivers/types";
import { driverTz } from "@/features/drivers/timezones";
import type { DashboardData, LeaderboardRow, MonthHires, WeekHires } from "@/features/dashboards/types";
import { snoozeOptions, taskMenu } from "@/features/tasks/menu";
import { Card } from "@/components/ui/card";
import { STAGE_BG, StatusPill } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/misc";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { toast } from "@/components/ui/toast";
import { PersonLink } from "@/components/PersonLink";
import { AddReminderModal as DriverReminderModal } from "@/pages/drivers/AddReminderModal";
import { QuickActivityModal } from "@/pages/drivers/QuickActivityModal";
import { useDriverActions } from "@/pages/drivers/useDriverActions";
import { ColumnBars, stepConversion } from "@/features/dashboards/blocks";
import { InfoTip } from "@/components/ui/info-tip";

// ---------------------------------------------------------------- sarlavha va karkas

export function HomeHero({ eyebrow, title, line, actions }: { eyebrow: ReactNode; title: ReactNode; line?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
      <div className="min-w-0 flex-1 basis-[360px]">
        <div className="text-[12px] font-semibold tracking-[0.06em] text-muted uppercase">{eyebrow}</div>
        <h1 className="mt-1.5 text-[40px] leading-[1.05] font-bold tracking-[-0.03em]">{title}</h1>
        {line && <p className="mt-2 text-[15px] text-muted">{line}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Bo'lim kartasi: sarlavha + izoh + o'ngda havola; `flush` -- ichidagi ro'yxat karta chetigacha boradi */
export function Section({ title, sub, action, children, className, flush, live }: { title: ReactNode; sub?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; flush?: boolean; live?: boolean }) {
  const { t } = useTranslation("home");
  return (
    <Card className={cn("flex flex-col", flush ? "pt-[18px] pb-1.5" : "p-5", className)}>
      <div className={cn("flex flex-wrap items-baseline gap-x-2.5 gap-y-1", flush ? "px-5 pb-2" : "pb-4")}>
        <h2 className="text-[19px] leading-snug font-semibold tracking-[-0.015em]">{title}</h2>
        {sub && <span className="text-[13px] text-muted">{sub}</span>}
        {live && (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted">
            <span className="live-dot h-[7px] w-[7px] rounded-full bg-accent" aria-hidden /> {t("attention.live")}
          </span>
        )}
        {action && <span className="ml-auto text-[13px] font-medium">{action}</span>}
      </div>
      {children}
    </Card>
  );
}

export function More({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-0.5 text-primary hover:underline">
      {children}
      <ArrowUpRight size={13} aria-hidden />
    </Link>
  );
}

export function Grid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("stagger grid gap-4", className)}>{children}</div>;
}

/** Sanaladigan raqam (600 ms); format -- butun son, pul va h.k. */
export function AnimatedNumber({ value, format }: { value: number; format?: (n: number) => string }) {
  const n = useCountUp(value);
  const rounded = Math.round(n);
  return <>{format ? format(rounded) : rounded.toLocaleString("en-US")}</>;
}

// ---------------------------------------------------------------- vazifalar (bugun)

type DialogState = { kind: "activity"; r: Reminder; type: "call" | "note" } | { kind: "reminder"; r: Reminder } | null;

export function TasksCard({ tasks, tz, phones, sub, className }: { tasks: NonNullable<DashboardData["tasks"]>; tz: string; phones?: boolean; sub?: ReactNode; className?: string }) {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation();
  const { t: tm } = useTranslation("menu");
  const { t: tt } = useTranslation("tasks");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const action = useReminderAction();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [done, setDone] = useState<Set<number>>(new Set());

  const rows: { r: Reminder; due: string; tone: "danger" | "muted" | "warning" }[] = [
    ...tasks.overdue.map((r) => ({ r, due: dueLabel(r, tz, t), tone: "danger" as const })),
    ...tasks.today.map((r) => ({ r, due: dayjs(r.effective_due).tz(tz).format("HH:mm"), tone: "muted" as const })),
    ...tasks.upcoming.map((r) => ({ r, due: dueLabel(r, tz, t), tone: r.kind === "cdl_expiry" || r.kind === "medical_expiry" ? ("warning" as const) : ("muted" as const) })),
  ];

  function refresh() {
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["counts"] });
    qc.invalidateQueries({ queryKey: ["reminder"] });
  }

  function markDone(r: Reminder) {
    setDone((s) => new Set(s).add(r.id));
    action.mutate(
      { id: r.id, action: "done" },
      {
        onSuccess: refresh,
        onError: () => {
          setDone((s) => {
            const next = new Set(s);
            next.delete(r.id);
            return next;
          });
          toast.error(t("today.doneFailed"));
        },
      },
    );
  }

  function snooze(r: Reminder, iso: string, label: string) {
    action.mutate({ id: r.id, action: "snooze", snooze_until: iso }, { onSuccess: () => { refresh(); toast.success(tt("toast.snoozed", { when: label })); }, onError: () => toast.error(tt("toast.updateFailed")) });
  }

  const menuFor = (r: Reminder): MenuEntry[] =>
    taskMenu(
      r,
      {
        openDriver: () => navigate(`/drivers/${r.driver}`),
        openDriverNewTab: () => openInNewTab(`/drivers/${r.driver}`),
        call: callPhone,
        copy: (text) => void copyText(text),
        done: () => markDone(r),
        undo: () => undefined,
        snooze: (o) => snooze(r, o.at.toISOString(), o.label),
        logActivity: (type) => setDialog({ kind: "activity", r, type }),
        addReminder: () => setDialog({ kind: "reminder", r }),
      },
      tm,
      snoozeOptions(tz, tt),
    );

  const tomorrow9 = () => dayjs().tz(tz).add(1, "day").hour(9).minute(0).second(0).millisecond(0);

  return (
    <Section
      className={className}
      flush
      title={t("today.title")}
      sub={
        <span className="inline-flex flex-wrap items-center gap-2">
          {tasks.counts.overdue > 0 && <span className="rounded-full bg-warning-soft px-2 py-0.5 text-[12px] font-semibold text-warning">{t("today.overdue", { count: tasks.counts.overdue })}</span>}
          <span>
            {t("today.dueToday", { count: tasks.counts.today })}
            {tasks.upcoming.length > 0 && ` · ${t("today.tomorrow", { count: tasks.upcoming.length })}`}
            {sub && <> · {sub}</>}
          </span>
        </span>
      }
      action={<More to="/tasks">{t("today.all")}</More>}
    >
      {rows.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2.5 px-5 py-10 text-center">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-success-soft text-success" aria-hidden>
            <Check size={18} strokeWidth={2.5} />
          </span>
          <p className="text-[13px] text-muted">{t("today.empty")}</p>
        </div>
      ) : (
        <ul className="px-2.5">
          {rows.map(({ r, due, tone }, i) => {
            const isDone = done.has(r.id);
            return (
              <ContextMenu key={r.id} items={() => menuFor(r)}>
                <li className={cn("group flex items-center gap-3.5 overflow-hidden rounded-2xl px-2.5 transition-colors hover:bg-fill/70", isDone && "task-done")}>
                  <button
                    type="button"
                    aria-label={t("today.markDone")}
                    disabled={isDone}
                    onClick={() => markDone(r)}
                    className={cn(
                      "grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] transition-colors",
                      isDone ? "check-pop border-accent bg-accent text-on-accent" : tone === "danger" ? "border-warning hover:bg-warning/10" : "border-faint hover:border-ink",
                    )}
                  >
                    {isDone && <Check size={14} strokeWidth={3} aria-hidden />}
                  </button>
                  <div className={cn("flex min-w-0 flex-1 items-center gap-3 py-3", i < rows.length - 1 && "border-b border-line group-hover:border-transparent")}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-medium">
                        {tc(`reminder.${r.kind}`, { defaultValue: r.kind })}
                        <span className="font-normal text-muted"> · </span>
                        <Link to={`/drivers/${r.driver}`} className="hover:underline">
                          {r.driver_name}
                        </Link>
                      </div>
                      {r.note && <div className="truncate text-[13px] text-muted">{r.note}</div>}
                    </div>
                    <span className="hidden shrink-0 items-center gap-1.5 group-hover:inline-flex">
                      {phones !== false && r.driver_phone && (
                        <button type="button" onClick={() => callPhone(r.driver_phone)} className="inline-flex h-7 items-center gap-1 rounded-full bg-ink px-3 text-[12px] font-semibold text-surface">
                          <Phone size={11} aria-hidden /> {t("today.call")}
                        </button>
                      )}
                      <button type="button" onClick={() => snooze(r, tomorrow9().toISOString(), t("today.snooze"))} className="inline-flex h-7 items-center rounded-full bg-raised px-3 text-[12px] font-medium text-ink shadow-pill">
                        {t("today.snooze")}
                      </button>
                    </span>
                    <span className={cn("tabular w-[76px] shrink-0 text-right text-[13px] font-semibold", tone === "danger" ? "text-warning" : tone === "warning" ? "text-warning" : "text-muted")}>{due}</span>
                  </div>
                </li>
              </ContextMenu>
            );
          })}
        </ul>
      )}
      {dialog?.kind === "activity" && <QuickActivityModal driver={{ id: dialog.r.driver, full_name: dialog.r.driver_name }} type={dialog.type} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog?.kind === "reminder" && <DriverReminderModal driverId={dialog.r.driver} dTz={driverTz(dialog.r.driver_state)} open onOpenChange={(o) => !o && setDialog(null)} defaultAssignee={dialog.r.assigned_to?.id ?? null} />}
    </Section>
  );
}

function dueLabel(r: Reminder, tz: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const due = dayjs(r.effective_due).tz(tz);
  const today = dayjs().tz(tz);
  if (due.isSame(today, "day")) return due.format("HH:mm");
  if (due.isSame(today.subtract(1, "day"), "day")) return t("due.yesterday");
  if (due.isSame(today.add(1, "day"), "day")) return t("due.tomorrow");
  return due.format("MMM D");
}

// ---------------------------------------------------------------- e'tibor signallari

export interface AttentionRow {
  key: string;
  count: ReactNode;
  text: string;
  sub?: string;
  tone: "bad" | "warn" | "good" | "ink";
  to: string;
}

export function AttentionCard({ rows, className }: { rows: AttentionRow[]; className?: string }) {
  const { t } = useTranslation("home");
  const { t: ti } = useTranslation("metrics");
  const toneClass = { bad: "text-danger", warn: "text-warning", good: "text-success", ink: "text-ink" };
  return (
    <Section className={className} flush title={t("attention.title")} live>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-muted">{t("attention.allClear")}</p>
      ) : (
        <ul>
          {rows.map((row, i) => (
            <li key={row.key}>
              <Link to={row.to} className="group flex items-center gap-3.5 px-5 hover:bg-fill/60">
                <span className={cn("flex min-w-0 flex-1 items-center gap-3.5 py-3", i < rows.length - 1 && "border-b border-line")}>
                  <span className={cn("tabular min-w-[64px] shrink-0 pr-1 text-[28px] leading-none font-light tracking-[-0.03em]", toneClass[row.tone])}>{row.count}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] leading-snug">
                      {row.text}
                      {/* Signal ta'rifi (metrics.attention.<key>): masalan "54/55" nimani anglatishi */}
                      {ti(`attention.${row.key}`, { defaultValue: "" }) && <InfoTip text={ti(`attention.${row.key}`)} className="ml-1.5" />}
                    </span>
                    {row.sub && <span className="block truncate text-[12px] text-muted">{row.sub}</span>}
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-faint transition-transform group-hover:translate-x-0.5" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Nomlar ro'yxati: "Dmitri V., Marcus D. va yana 3 ta" */
export function sampleLine(sample: { name: string }[] | undefined, count: number, t: (k: string, o?: Record<string, unknown>) => string): string | undefined {
  if (!sample || sample.length === 0) return undefined;
  const names = sample.map((s) => s.name).join(", ");
  const rest = count - sample.length;
  return rest > 0 ? `${names} ${t("attention.andMore", { count: rest })}` : names;
}

// ---------------------------------------------------------------- pipeline lentasi

const STRIP_ORDER = PIPELINE_ORDER.filter((s) => s !== "hired");

export function StageStrip({ stages, title, sub, to, className }: { stages: DashboardData["stages"]; title: string; sub: ReactNode; to: string; className?: string }) {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation();
  const items = STRIP_ORDER.map((s) => ({ status: s, count: stages[s] ?? 0 })).filter((x) => x.count > 0);
  const total = items.reduce((n, x) => n + x.count, 0);
  return (
    <Section className={className} title={title} sub={sub} action={<More to={to}>{t("pipeline.board")}</More>}>
      {total === 0 ? (
        <p className="text-[13px] text-muted">{t("pipeline.empty")}</p>
      ) : (
        <>
          <div className="flex h-3.5 gap-[3px] overflow-hidden rounded-full">
            {items.map((x) => (
              <span key={x.status} className={cn("grow-x block min-w-[6px] rounded-sm", STAGE_BG[x.status])} style={{ flex: x.count }} title={`${tc(`status.${x.status}`)} · ${x.count}`} />
            ))}
          </div>
          <div className="mt-3.5 flex flex-wrap gap-x-5 gap-y-1.5">
            {STRIP_ORDER.map((s) => (
              <Link key={s} to={`/drivers?status=${s}`} className="inline-flex items-center gap-2 text-[13px] hover:underline">
                <span className={cn("h-2 w-2 rounded-full", STAGE_BG[s], !(stages[s] ?? 0) && "opacity-30")} aria-hidden />
                <span className="text-muted">{tc(`status.${s}`)}</span>
                <span className="tabular font-semibold">{stages[s] ?? 0}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- oxirgi o'zgarishlar va reyting

export function RecentCard({ drivers, className }: { drivers: DriverListItem[]; className?: string }) {
  const { t } = useTranslation("home");
  const actions = useDriverActions();
  return (
    <Section className={className} flush title={t("recent.title")} action={<More to="/drivers">{t("recent.all")}</More>}>
      {drivers.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-muted">{t("recent.empty")}</p>
      ) : (
        <ul>
          {drivers.map((d, i) => (
            <ContextMenu key={d.id} items={() => actions.items(d)}>
              <li>
                <Link to={`/drivers/${d.id}`} className="flex items-center gap-3.5 px-5 hover:bg-fill/60">
                  <Avatar initials={initials(d.full_name)} size={36} className="shrink-0" />
                  <span className={cn("flex min-w-0 flex-1 items-center gap-3 py-2.5", i < drivers.length - 1 && "border-b border-line")}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-medium">{d.full_name}</span>
                      <span className="block truncate text-[13px] text-muted">{[d.state, d.cdl_class ? `Class ${d.cdl_class}` : "", d.current_job?.carrier_name].filter(Boolean).join(" · ") || "—"}</span>
                    </span>
                    <StatusPill status={d.status} />
                    <span className="w-16 shrink-0 text-right text-[13px] text-muted">{formatWhen(d.status_changed_at)}</span>
                  </span>
                </Link>
              </li>
            </ContextMenu>
          ))}
        </ul>
      )}
      {actions.dialogs}
    </Section>
  );
}

export function LeaderCard({ rows, meId, title, sub, footer, menu, className }: { rows: LeaderboardRow[]; meId?: number; title: ReactNode; sub?: ReactNode; footer?: ReactNode; menu?: (r: LeaderboardRow) => MenuEntry[]; className?: string }) {
  const { t } = useTranslation("home");
  return (
    <Section className={className} title={title} sub={sub}>
      {rows.length === 0 && <p className="text-[13px] text-muted">{t("leader.none")}</p>}
      <div className="-mx-2.5 flex flex-col">
        {rows.map((r, i) => {
          const mine = meId !== undefined && r.user_id === meId;
          const row = (
            <div className={cn("flex items-center gap-3 rounded-xl px-2.5 py-2", mine && "bg-accent-soft/60")}>
              <span className="tabular w-4 text-[14px] font-semibold text-muted">{i + 1}</span>
              <Avatar initials={initials(r.display_name)} size={34} className={cn("shrink-0", mine && "ring-2 ring-accent")} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium">
                  <PersonLink id={r.user_id} name={r.display_name} />
                  {mine && <span className="text-muted"> {t("leader.you")}</span>}
                </span>
                {r.branch && <span className="block truncate text-[12px] text-muted">{r.branch}</span>}
              </span>
              <span className="tabular text-[24px] leading-none font-light tracking-[-0.03em]">{r.hires}</span>
            </div>
          );
          return menu ? (
            <ContextMenu key={r.user_id} items={() => menu(r)}>
              {row}
            </ContextMenu>
          ) : (
            <div key={r.user_id}>{row}</div>
          );
        })}
      </div>
      {footer && <div className="mt-auto flex items-center gap-2 border-t border-line pt-3 text-[13px] text-muted">{footer}</div>}
    </Section>
  );
}

export function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

// ---------------------------------------------------------------- grafiklar

export function MonthsCard({ months, className }: { months: MonthHires[]; className?: string }) {
  const { t } = useTranslation("home");
  const total = months.reduce((n, m) => n + m.hires, 0);
  return (
    <Section className={className} title={t("months.title")} sub={t("months.sub", { count: total })} action={<More to="/reports?tab=trends">{t("months.trends")}</More>}>
      <ColumnBars items={months.map((m) => ({ key: m.period, label: dayjs(`${m.period}-01`).format("MMM"), value: m.hires, title: dayjs(`${m.period}-01`).format("MMMM YYYY") }))} height={190} />
    </Section>
  );
}

export function WeeksCard({ weeks, className }: { weeks: WeekHires[]; className?: string }) {
  const { t } = useTranslation("home");
  return (
    <Section className={className} title={t("weeks.title")} sub={t("weeks.sub")}>
      <ColumnBars items={weeks.map((w) => ({ key: w.week, label: dayjs(w.week).format("D MMM"), value: w.hires }))} height={170} />
    </Section>
  );
}


export function FunnelCard({ data, month, period, className }: { data: DashboardData; month: string; period: string; className?: string }) {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation();
  const steps = data.funnel;
  const max = Math.max(1, ...steps.map((s) => s.count));
  // Takrorsiz driverlar (backend `lost`); eski javobda -- sabablar yig'indisi
  const lost = data.lost ?? data.loss_reasons.reduce((n, r) => n + r.count, 0);
  return (
    <Section className={className} title={t("funnel.title")} sub={t("funnel.sub", { month })}>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={s.status} className="space-y-1.5">
            <Link to={stageMonthLink(s.status as DriverStatus, period)} className="flex items-baseline gap-2 text-[13px] hover:underline">
              <span className="flex-1 text-muted">{tc(`status.${s.status}`, { defaultValue: s.label })}</span>
              <span className="tabular text-[15px] font-semibold">{s.count}</span>
              <span className="tabular w-11 text-right text-muted">{stepConversion(steps, i) === null ? "" : `${stepConversion(steps, i)}%`}</span>
            </Link>
            <div className="h-1.5 overflow-hidden rounded-full bg-fill">
              <div className={cn("grow-x h-full rounded-full", i === steps.length - 1 ? "bg-success" : "bg-chart")} style={{ width: `${Math.max(2, (s.count / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
      {lost > 0 && (
        // Boshqa qatorlar kabi bosiladi: shu oyda yo'qotilganlar ro'yxati
        <Link to={stageMonthLink("lost", period)} className="mt-4 block text-[13px] text-muted hover:underline">
          {t("funnel.lost", { count: lost })} · {data.loss_reasons.map((r) => r.reason).join(", ")}
        </Link>
      )}
    </Section>
  );
}

export type { DriverStatus };
